<script setup lang="ts">
import { ErrorMessage, Field, Form } from "vee-validate";
import { ref } from "vue";
import { object, string, ref as yupRef } from "yup";
import countries from "@/data/countries.json";
import countryCodes from "@/data/country-codes.json";
import "v-calendar/style.css";
const site = useSite();
const page = usePage().value;

const dateTime = ref(new Date().toLocaleString());
const title = ref("");
const firstName = ref("");
const lastName = ref("");
const email = ref("");
const confirmEmail = ref("");
const countryCode = ref("");
const phone = ref("");
const dobDay = ref("");
const dobMonth = ref("");
const dobYear = ref("");
const country = ref("");
const enquiry = ref("");
const competitionYear = ref(new Date().getFullYear());
const honeypot = ref("");

const agreeTerms = ref("");
const marketingConsent = ref<string>("");
const ciceroneNewsletterConsent = ref<string>("");

const emailContact = ref(site.value?.emailContact);
const monthOptions = [
  { value: "01", label: "January" },
  { value: "02", label: "February" },
  { value: "03", label: "March" },
  { value: "04", label: "April" },
  { value: "05", label: "May" },
  { value: "06", label: "June" },
  { value: "07", label: "July" },
  { value: "08", label: "August" },
  { value: "09", label: "September" },
  { value: "10", label: "October" },
  { value: "11", label: "November" },
  { value: "12", label: "December" },
];
const dayOptions = Array.from({ length: 31 }, (_, i) => {
  const day = String(i + 1).padStart(2, "0");
  return { value: day, label: String(i + 1) };
});

const emailRegex = /^[\w.%+-]+@[a-z0-9.-]+\.[a-z]{2,}$/i;

const schema = object().shape({
  title: string().required("Please enter title"),
  firstName: string().required("Please enter first name"),
  lastName: string().required("Please enter last name"),
  email: string()
    .matches(emailRegex, "Please enter a valid email address")
    .required("Please enter an email"),
  confirmEmail: string()
    .oneOf([yupRef("email")], "Emails must match")
    .required("Please confirm your email"),
  countryCode: string().required("Please select country code"),

  phone: string()
    .required("Please enter a phone number")
    .matches(/^[0-9\s\-]{7,15}$/, "Please enter a valid phone number"),

  dobDay: string()
    .required("Please select day")
    .matches(/^(0?[1-9]|[12]\d|3[01])$/, "Please enter a valid day"),
  dobMonth: string()
    .required("Please select month")
    .matches(/^(0?[1-9]|1[0-2])$/, "Please enter a valid month"),
  dobYear: string()
    .required("Please select year")
    .matches(/^\d{4}$/, "Please enter a valid year"),

  country: string().required("Please select country"),
  enquiry: string().required("Please enter your enquiry"),
  //   agreeTerms: string()
  //     .required("Please agree to the terms")
  //     .oneOf(
  //       [
  //         "I agree with Mont Blanc Trek's privacy statement which can be found in the footer of this page",
  //       ],
  //       "Please agree to the terms",
  //     ),
});

const marketingConsentChange = (event: Event) => {
  const target = event.target as HTMLInputElement;
  if (target?.checked) {
    marketingConsent.value = "Yes please";
  }
  if (!target?.checked) {
    marketingConsent.value = "No thanks";
  }
};

const ciceroneNewsletterConsentChange = (event: Event) => {
  const target = event.target as HTMLInputElement;
  if (target?.checked) {
    ciceroneNewsletterConsent.value = "Yes please";
  }
  if (!target?.checked) {
    ciceroneNewsletterConsent.value = "No thanks";
  }
};

const sending = ref(false);
const emailSent = ref(false);
const submitError = ref("");

const onSubmit = async (_values: Record<string, unknown>) => {
  if (honeypot.value !== "") return;

  sending.value = true;
  submitError.value = "";
  const dateOfBirth =
    dobDay.value && dobMonth.value && dobYear.value
      ? `${dobDay.value.padStart(2, "0")}/${dobMonth.value.padStart(2, "0")}/${
          dobYear.value
        }`
      : "";

  const payload = {
    title: title.value.trim() || undefined,
    first_name: firstName.value.trim(),
    last_name: lastName.value.trim(),
    email: email.value.trim(),
    country: country.value.trim() || undefined,
    country_code: countryCode.value.trim() || undefined,
    phone: phone.value.trim() || undefined,
    dob_day: dobDay.value.trim() || undefined,
    dob_month: dobMonth.value.trim() || undefined,
    dob_year: dobYear.value.trim() || undefined,
    date_of_birth: dateOfBirth || undefined,
    message: enquiry.value.trim() || undefined,
    marketing_consent: marketingConsent.value || undefined,
    cicerone_newsletter_consent: ciceroneNewsletterConsent.value || undefined,
    email_text: (page?.emailText ?? "").trim() || undefined,
    competition_year: competitionYear.value,
    source_page: "/competitions",
    date_time: new Date().toISOString(),
    user_agent: navigator.userAgent,
  };

  try {
    await $fetch("/api/competitions/create", {
      method: "POST",
      body: payload,
    });
    emailSent.value = true;
  } catch (err: unknown) {
    const data =
      err && typeof err === "object" && "data" in err
        ? (err.data as { message?: string; statusMessage?: string } | undefined)
        : undefined;
    const message =
      data?.message ||
      data?.statusMessage ||
      "Something went wrong. Please try again.";
    submitError.value = message;
  } finally {
    sending.value = false;
  }
};
</script>

<template>
  <section class="comp-entry-form">
    <ClientOnly>
      <h3>Competition Entry Form</h3>
      <div class="contact-form">
        <Form
          v-if="!emailSent"
          id="myForm"
          :validation-schema="schema"
          @submit="onSubmit"
        >
          <input v-model="dateTime" type="hidden" name="dateTime" />
          <input v-model="emailContact" type="hidden" name="emailContact" />
          <input v-model="competitionYear" type="hidden" name="competitionYear" />
          <input
            v-model="honeypot"
            type="text"
            name="website"
            class="honeypot-field"
            autocomplete="off"
            tabindex="-1"
            aria-hidden="true"
          />
          <div class="form-col col-3">
            <div class="form-control">
              <label>Title</label>
              <div class="select-wrapper">
                <Field v-model="title" as="select" name="title" class="select-styled">
                  <option value="" disabled>Please select...</option>
                  <option value="Mr">Mr</option>
                  <option value="Mrs">Mrs</option>
                  <option value="Ms">Ms</option>
                  <option value="Miss">Miss</option>
                  <option value="Mx">Mx</option>
                </Field>
              </div>
              <Transition name="error" appear>
                <ErrorMessage class="error" name="title" />
              </Transition>
            </div>
            <div class="form-control">
              <label>First Name</label>
              <Field
                id="firstName"
                v-model="firstName"
                type="text"
                label="First name"
                name="firstName"
                placeholder="First name*"
              />
              <Transition name="error" appear>
                <ErrorMessage class="error" name="firstName" />
              </Transition>
            </div>
            <div class="form-control">
              <label>Last Name</label>
              <Field
                id="lastName"
                v-model="lastName"
                type="text"
                label="Last name"
                name="lastName"
                placeholder="Last name*"
              />
              <Transition name="error" appear>
                <ErrorMessage class="error" name="lastName" />
              </Transition>
            </div>
          </div>

          <div class="form-col col-2">
            <div class="form-control">
              <label>Email</label>
              <Field
                id="email"
                v-model="email"
                type="email"
                name="email"
                label="Email"
                placeholder="Email*"
              />
              <Transition name="error" appear>
                <ErrorMessage class="error" name="email" />
              </Transition>
            </div>
            <div class="form-control">
              <label>Confirm Email</label>
              <Field
                id="confirmEmail"
                v-model.trim="confirmEmail"
                name="confirmEmail"
                type="email"
                placeholder="Re-type Email"
              />
              <Transition name="error" appear>
                <ErrorMessage class="error" name="confirmEmail" />
              </Transition>
            </div>
          </div>
          <div class="form-col col-4">
            <div class="form-control">
              <label>Int. Dialling Code</label>
              <div class="select-wrapper">
                <Field
                  v-model="countryCode"
                  as="select"
                  name="countryCode"
                  class="select-styled"
                >
                  <option value="" disabled>Please select...</option>
                  <option
                    v-for="code in countryCodes"
                    :key="code.value"
                    :value="code.value"
                  >
                    {{ code.name }}
                  </option>
                </Field>
              </div>
              <Transition name="error" appear>
                <ErrorMessage class="error" name="countryCode" />
              </Transition>
            </div>
            <div class="form-control">
              <label>Telephone</label>
              <Field
                id="phone"
                v-model="phone"
                type="tel"
                name="phone"
                label="Phone"
                placeholder="Phone number"
              />
              <Transition name="error" appear>
                <ErrorMessage class="error" name="phone" />
              </Transition>
            </div>
            <div class="form-control">
              <label>Date of Birth</label>
              <div class="form-col col-3">
                <Field
                  id="dobDay"
                  v-model="dobDay"
                  as="select"
                  name="dobDay"
                  class="select-styled"
                >
                  <option value="" disabled>Day</option>
                  <option
                    v-for="day in dayOptions"
                    :key="day.value"
                    :value="day.value"
                  >
                    {{ day.label }}
                  </option>
                </Field>
                <Field
                  id="dobMonth"
                  v-model="dobMonth"
                  as="select"
                  name="dobMonth"
                  class="select-styled"
                >
                  <option value="" disabled>Month</option>
                  <option
                    v-for="month in monthOptions"
                    :key="month.value"
                    :value="month.value"
                  >
                    {{ month.label }}
                  </option>
                </Field>
                <Field
                  id="dobYear"
                  v-model="dobYear"
                  type="text"
                  name="dobYear"
                  placeholder="YYYY"
                  maxlength="4"
                />
              </div>
              <Transition name="error" appear>
                <ErrorMessage class="error" name="dobDay" />
              </Transition>
              <Transition name="error" appear>
                <ErrorMessage class="error" name="dobMonth" />
              </Transition>
              <Transition name="error" appear>
                <ErrorMessage class="error" name="dobYear" />
              </Transition>
            </div>
            <div class="form-control">
              <label>Country of Residence</label>
              <div class="select-wrapper country">
                <Field
                  v-model.trim="country"
                  as="select"
                  name="country"
                  class="select-styled"
                >
                  <option value="" disabled>Please select...</option>
                  <option v-for="code in countries" :key="code.value" :value="code.value">
                    {{ code.label }}
                  </option>
                </Field>
                <Transition name="error" appear>
                  <ErrorMessage class="error" name="country" />
                </Transition>
              </div>
            </div>
          </div>

          <div class="form-col col-1">
            <div class="form-control">
              <label
                >Why would this trip be special for you? (answer in 5 to 20 words)</label
              >
              <Field
                id="enquiry"
                v-model="enquiry"
                as="textarea"
                label="enquiry"
                type="textarea"
                class="textarea"
                name="enquiry"
                placeholder="Why would this trip be special for you?..."
              />
              <Transition name="error" appear>
                <ErrorMessage class="error" name="enquiry" />
              </Transition>
            </div>
          </div>

          <div class="checkbox-wrap">
            <div class="form-col col-3">
              <div class="form-control">
                <AppFormCustomCheckbox v-model="agreeTerms" name="agreeTerms">
                  I agree with Mont Blanc Trek's
                  <a href="/competition-terms-and-conditions" rel="noopener" class="link">
                    competition terms & conditions
                  </a>
                </AppFormCustomCheckbox>
                <Transition name="error" appear>
                  <ErrorMessage class="error" name="agreeTerms" />
                </Transition>
              </div>
              <div class="form-control">
                <AppFormCustomCheckbox
                  v-model="marketingConsent"
                  name="marketingConsent"
                  value="Sign up to our newsletter"
                  @change="marketingConsentChange($event)"
                />
              </div>
              <div class="form-control">
                <AppFormCustomCheckbox
                  v-model="ciceroneNewsletterConsent"
                  name="ciceroneNewsletterConsent"
                  value="Sign up to the Cicerone newsletter"
                  @change="ciceroneNewsletterConsentChange($event)"
                />
              </div>
            </div>
          </div>
          <Field v-model="marketingConsent" type="hidden" name="marketingConsent" />
          <Field
            v-model="ciceroneNewsletterConsent"
            type="hidden"
            name="ciceroneNewsletterConsent"
          />
          <div v-if="submitError" class="form-error">
            {{ submitError }}
          </div>
          <button class="submit-btn btn" type="submit" :disabled="sending">
            <span v-if="sending">Sending...</span>
            <span v-else>Send</span>
          </button>
        </Form>
        <div v-else>
          <Transition name="slideleft" appear>
            <div v-if="emailSent" id="formEndScreen">
              <h4>Thank you for entering our competition!</h4>
            </div>
          </Transition>
        </div>
      </div>
    </ClientOnly>
  </section>
</template>

<style scoped>
.honeypot-field {
  position: absolute;
  left: -9999px;
  width: 1px;
  height: 1px;
  overflow: hidden;
  opacity: 0;
  pointer-events: none;
}

.form-error {
  margin: 0 0 16px;
  padding: 12px 14px;
  border-radius: 6px;
  background: #b30000;
  color: #fff;
  font-weight: 600;
}
</style>
